// kg/ha NUNCA é armazenado — é sempre derivado da área APLICADA, nunca da área total
// (Rizoma_Nutricao_Fase0_Spec.md §2.3). Função única, usada por server e client, pra
// não deixar essa conta divergir entre a tela e o backend.

/** Área efetivamente aplicada = área do setor (snapshot) × percentual aplicado. */
export function areaAplicadaHa(areaHaSnapshot: number, percentualAplicado: number): number {
  return areaHaSnapshot * (percentualAplicado / 100);
}

/** kg/ha derivado do kg total sobre a área APLICADA. null quando a área é zero/negativa. */
export function kgPorHa(kgTotal: number, areaHaSnapshot: number, percentualAplicado: number): number | null {
  const area = areaAplicadaHa(areaHaSnapshot, percentualAplicado);
  if (area <= 0) return null;
  return kgTotal / area;
}

// ─── Rateio por setor — DERIVADO, nunca armazenado ────────────────────────────
// A quantidade mora só em mnj_operacao_item, no nível da OPERAÇÃO (kg total sobre
// a área somada dos setores cobertos). O quanto cabe a cada setor é sempre esta
// conta, feita na hora: kg_setor = kg/ha × área aplicada do setor. Existe aqui —
// e não dentro de um componente — porque a mesma conta é mostrada ao vivo no
// formulário de criação manual e usada pra conferir o commit; duas implementações
// divergiriam (é a mesma família de bug que dobrou a folha no Equipe).

export interface SetorAreaAplicada {
  setorId: string;
  areaHaSnapshot: number;
  percentualAplicado: number;
}

export interface RateioSetor {
  setorId: string;
  areaAplicadaHa: number;
  kg: number;
}

/** Soma da área APLICADA de todos os setores da operação (área × percentual). */
export function areaAplicadaTotalHa(setores: SetorAreaAplicada[]): number {
  return setores.reduce((acc, s) => acc + areaAplicadaHa(s.areaHaSnapshot, s.percentualAplicado), 0);
}

/**
 * Distribui o kg TOTAL da operação entre os setores cobertos, a partir da dose única
 * por hectare (§1.2c: uma operação = uma dose por hectare pra todos os setores).
 * `kgPorHa` é null — e o rateio vem zerado — quando a área aplicada total é zero;
 * nunca divide por zero nem inventa dose. Soma de `porSetor[].kg` == kgTotal.
 */
export function ratearKgPorSetor(
  kgTotal: number,
  setores: SetorAreaAplicada[],
): { kgPorHa: number | null; areaAplicadaTotalHa: number; porSetor: RateioSetor[] } {
  const areaTotal = areaAplicadaTotalHa(setores);
  const dose = areaTotal > 0 ? kgTotal / areaTotal : null;
  return {
    kgPorHa: dose,
    areaAplicadaTotalHa: areaTotal,
    porSetor: setores.map(s => {
      const area = areaAplicadaHa(s.areaHaSnapshot, s.percentualAplicado);
      return { setorId: s.setorId, areaAplicadaHa: area, kg: dose === null ? 0 : dose * area };
    }),
  };
}
